import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { Loader2, X } from "lucide-react";
import { clearProjectCache } from "@/components/ProjectPicker";

const COLOR_PALETTE = [
  "#2aa39a",
  "#7e57a3",
  "#f59e0b",
  "#ef4444",
  "#3b82f6",
  "#10b981",
  "#ec4899",
  "#6366f1",
  "#84cc16",
  "#f97316",
];

export default function EditProjectDialog({ open, project, onClose, onSaved }) {
  const isEdit = !!project;
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [color, setColor] = useState(COLOR_PALETTE[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setName(project?.name || "");
      setClient(project?.client || "");
      setColor(project?.color || COLOR_PALETTE[0]);
      setError("");
    }
  }, [open, project]);

  if (!open) return null;

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Project name is required.");

    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        client: client.trim() || null,
        color,
      };
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/admin/projects/${project.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/admin/projects", payload);
        saved = data;
      }
      clearProjectCache();
      setSubmitting(false);
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save project.");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      onClick={handleClose}
      data-testid="edit-project-dialog"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 text-white rounded-t-2xl flex items-center justify-between"
          style={{
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
          }}
        >
          <h3 className="text-base font-semibold">
            {isEdit ? "Edit project" : "Add project"}
          </h3>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white"
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Project name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. GLP-1 Launch"
              data-testid="project-name-input"
              maxLength={100}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={submitting}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Client (optional)
            </label>
            <input
              type="text"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              placeholder="e.g. Internal, or a client name"
              maxLength={100}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  disabled={submitting}
                  aria-label={`Use color ${c}`}
                  className={`w-7 h-7 rounded-full border-2 transition ${
                    color === c
                      ? "border-slate-700 ring-2 ring-offset-1"
                      : "border-white shadow-sm"
                  }`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
          {error && (
            <div className="text-sm px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="project-submit-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition disabled:opacity-70"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
              }}
            >
              {submitting && <Loader2 className="animate-spin" size={14} />}
              {isEdit ? "Save changes" : "Create project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
